import React, { useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import LockIcon from "@mui/icons-material/Lock";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import VerifiedIcon from "@mui/icons-material/Verified";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { ApprovalStatus } from "../../Enums/enums";
import { ApprovalBadge, TimeAgo, WcaBadge, metaFor } from "./constants";

// Overlay icon buttons sit on the thumbnail; on touch they need to be big
// enough to hit without zooming, so they grow their padding below sm.
const OVERLAY_BTN_SX = {
  bgcolor: "rgba(2,6,23,0.5)",
  color: "white",
  p: { xs: 0.85, sm: 0.5 },
};

// Every footer button clears the ~40px touch-target floor on a phone.
const ACTION_BTN_SX = {
  borderRadius: 2,
  textTransform: "none",
  fontSize: 12.5,
  minHeight: { xs: 40, sm: 32 },
};

/**
 * One compilation, thumbnail-first — the frame is what an approver actually
 * judges. A colour rail on the left encodes approval status so a wall of
 * cards can be read without stopping to parse each badge.
 */
export default function CompilationCard({
  compilation, isAdmin,
  onPreview, onManageWgids, onEdit, onDelete, onApprove, onReject,
}) {
  const [approving, setApproving] = useState(false);
  const wgids = compilation.wgids || [];
  const status = compilation.compilation_approval_status;
  const meta = metaFor(status);
  const isPending = status === ApprovalStatus.PENDING_APPROVAL;
  const isRejected = status === ApprovalStatus.REJECTED;
  const playable = Boolean(compilation.video_url);

  const handleApprove = async (e) => {
    e.stopPropagation();
    setApproving(true);
    try {
      await onApprove();
    } finally {
      setApproving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        border: "1px solid #e6ebf1",
        transition: "box-shadow .18s ease, transform .18s ease, border-color .18s ease",
        "&:hover": {
          boxShadow: "0 10px 24px -12px rgba(15,23,42,.28)",
          transform: "translateY(-2px)",
          borderColor: "#cbd5e1",
        },
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0, top: 0, bottom: 0,
          width: 3,
          bgcolor: meta.color,
          zIndex: 2,
        },
      }}
    >
      <Box
        onClick={playable ? onPreview : undefined}
        sx={{
          // Taller on a phone, where the card spans the full column width and
          // a 158px band would read as a letterboxed sliver.
          height: { xs: 200, sm: 158 },
          bgcolor: "#0b1120",
          position: "relative",
          cursor: playable ? "pointer" : "default",
          "&:hover .cmp-play": { opacity: 1 },
        }}
      >
        {playable ? (
          <video
            src={compilation.video_url}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            muted
            preload="metadata"
          />
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={0.5}>
            <VideoLibraryIcon sx={{ color: "#334155", fontSize: 34 }} />
            <Typography variant="caption" sx={{ color: "#475569" }}>
              {isRejected ? "Video deleted on rejection" : "No video"}
            </Typography>
          </Box>
        )}

        {playable && (
          <Box
            className="cmp-play"
            sx={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(180deg, rgba(2,6,23,.15), rgba(2,6,23,.55))",
              opacity: 0, transition: "opacity .18s",
            }}
          >
            <Box
              sx={{
                width: 46, height: 46, borderRadius: "50%", bgcolor: "rgba(255,255,255,.92)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <PlayArrowIcon sx={{ color: "#0f172a" }} />
            </Box>
          </Box>
        )}

        <Box sx={{ position: "absolute", top: 8, left: 10, display: "flex", gap: 0.5, alignItems: "center" }}>
          <WcaBadge size="small" tone="light">{compilation.unique_key}</WcaBadge>
          {compilation.is_staged && (
            <Tooltip title="Uploaded but private — it becomes publicly readable only when approved.">
              <Box
                sx={{
                  display: "flex", alignItems: "center", gap: 0.3, px: 0.6, py: 0.4, borderRadius: 1.5,
                  bgcolor: "rgba(2,6,23,.55)", color: "#e2e8f0", cursor: "help",
                  border: "1px solid rgba(255,255,255,.25)", backdropFilter: "blur(4px)",
                }}
              >
                <LockIcon sx={{ fontSize: 11 }} />
                <Typography variant="caption" sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3 }}>
                  PRIVATE
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 0.5 }}>
          <Tooltip title="Edit compilation">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              sx={{ ...OVERLAY_BTN_SX, "&:hover": { bgcolor: "#2563eb" } }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete compilation">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              sx={{ ...OVERLAY_BTN_SX, "&:hover": { bgcolor: "#dc2626" } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          p: { xs: 1.5, sm: 1.75 }, pl: { xs: 1.75, sm: 2 },
          display: "flex", flexDirection: "column", flex: 1, gap: 1, minWidth: 0,
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700} noWrap title={compilation.title}>
            {compilation.title}
          </Typography>
          {compilation.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                overflow: "hidden", mt: 0.25,
              }}
            >
              {compilation.description}
            </Typography>
          )}
        </Box>

        <Box display="flex" gap={0.5} flexWrap="wrap">
          <ApprovalBadge status={status} full={false} />
          {compilation.brand && (
            <Chip
              size="small"
              icon={<BusinessIcon sx={{ fontSize: 13 }} />}
              label={compilation.brand}
              sx={{ height: 22, fontSize: 11, "& .MuiChip-label": { px: 0.8 } }}
            />
          )}
          <Tooltip
            title={wgids.length ? wgids.map((w) => w.wgid).join(", ") : "No source WGIDs attached"}
          >
            <Chip
              size="small"
              icon={<VerifiedIcon sx={{ fontSize: 13, color: wgids.length ? "#16a34a !important" : undefined }} />}
              label={`${wgids.length} WGID${wgids.length !== 1 ? "s" : ""}`}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                cursor: "help",
                color: wgids.length ? "#15803d" : "#64748b",
                bgcolor: wgids.length ? "#f0fdf4" : "#f1f5f9",
                border: `1px solid ${wgids.length ? "#bbf7d0" : "#e2e8f0"}`,
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          </Tooltip>
        </Box>

        {isRejected && compilation.compilation_rejection_reason && (
          <Box sx={{ px: 1, py: 0.6, borderRadius: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
            <Tooltip title={compilation.compilation_rejection_reason}>
              <Typography
                variant="caption"
                sx={{
                  color: "#b91c1c", display: "-webkit-box", WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical", overflow: "hidden",
                }}
              >
                {compilation.compilation_rejection_reason}
              </Typography>
            </Tooltip>
          </Box>
        )}

        <Typography variant="caption" color="text.disabled" noWrap>
          {compilation.username ? `by ${compilation.username} · ` : ""}
          <TimeAgo iso={compilation.created_date} />
        </Typography>

        <Box flex={1} />

        {isAdmin && isPending && (
          <Box display="flex" gap={1}>
            <Tooltip title="Approve — moves the video to its public S3 path">
              <span style={{ flex: 1 }}>
                <Button
                  size="small" fullWidth variant="contained" disableElevation
                  startIcon={approving ? <CircularProgress size={13} color="inherit" /> : <ThumbUpAltIcon sx={{ fontSize: 15 }} />}
                  onClick={handleApprove} disabled={approving}
                  sx={{ ...ACTION_BTN_SX, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" } }}
                >
                  {approving ? "Publishing…" : "Approve"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Reject — deletes the uploaded video">
              <span>
                <Button
                  size="small" variant="outlined" color="error"
                  onClick={(e) => { e.stopPropagation(); onReject(); }} disabled={approving}
                  sx={{ ...ACTION_BTN_SX, minWidth: 0, px: 1.6 }}
                >
                  <ThumbDownAltIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}

        <Box display="flex" gap={0.75}>
          <Button
            size="small" variant="outlined" color="inherit" onClick={onManageWgids}
            startIcon={<LinkIcon sx={{ fontSize: 15 }} />}
            sx={{ ...ACTION_BTN_SX, flex: 1, borderColor: "#e2e8f0" }}
          >
            WGIDs
          </Button>
          <Button
            size="small" variant="outlined" color="inherit" disabled={!playable}
            startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />} onClick={onPreview}
            sx={{ ...ACTION_BTN_SX, flex: 1, borderColor: "#e2e8f0" }}
          >
            Preview
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
